/**
 * The venues screens (И2, task T.C4a): the roster and, when `?id=` is set, the
 * venue card.
 *
 * ROUTER DECISION — the card is a QUERY PARAM (`/venues?id=12`), not a path
 * (`/venues/12`). T.C4a owns `pages/venues/**`; the route table lives in
 * `router.tsx`, which another track owns, and a second route would also need a
 * shell-level decision about what "back" means. The param keeps the drill
 * inside one route (so the roster's filters survive the round trip for free),
 * keeps the deep link shareable (`/venues?id=12&region=Москва`) and needs no
 * router edit. Moving to `/venues/:id` later is a one-line route + a
 * `useParams` swap behind `cardParams()` — nothing else reads the URL shape.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';

import type { ApiError } from '../../api/client';
import { useVenues } from '../../api/venues';
import { useUserDirectory } from '../../api/venue-links';
import { ExportButton } from '../../components/ExportButton';
import { Combobox } from '../../components/Combobox';
import { Badge, EmptyState, ErrorState, Input, Pagination, Select } from '../../components/ui';
import type { VenueKind } from '../../api/venues';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams } from '../../lib/query-params';
import { useVenueDirectory } from '../quotas/dictionary';
import { venueComboboxOptions } from '../quotas/quotas-model';

import { countVenueDepartments, departmentFilterOptions, parseDepartmentBucket } from '../../departments';
import type { DepartmentBucket, DepartmentSource, VenueManagersLike } from '../../departments';
import { OnlinePivotBlock } from './OnlinePivotBlock';
import { VenueCard } from './VenueCard';
import { VenueStatusBadges } from './VenueStatusBadges';
import './venues.css';
import {
  DEFAULT_ROSTER_COLUMNS,
  PARAM_KIND,
  PARAM_PAGE,
  PARAM_Q,
  PARAM_REGION,
  PARAM_VENUE_ID,
  ROSTER_COLUMNS,
  ROSTER_COLUMNS_STORAGE_KEY,
  cardParams,
  controlStatusLabel,
  PARAM_DEPARTMENT,
  curatorNameLabel,
  describeFailure,
  isPartnerVenue,
  managerCell,
  parseKindParam,
  parseVisibleColumns,
  rosterColumnLabels,
  rosterHeaderColumns,
  venuesOfDepartment,
  rowStatusCounts,
  serializeVisibleColumns,
  sheetCellLabel,
  toListQuery,
  totalPages,
  venueCardPath,
  venueCountLabel,
  venueKindCounts,
  venueKindFilterOptions,
  venueKindLabel,
  venuesOfKind,
  type RosterColumnId,
  type VenuesListParams,
  type VenueRow,
} from './venues-model';

/** The hover text of the kind badge — the two kinds, in the SR's own words. */
function venueKindTitle(venue: Pick<VenueKindHolder, 'kind' | 'partnerName'>): string {
  return venue.kind === 'PARTNER_ONLINE'
    ? `Филиал партнёра${venue.partnerName ? ` «${venue.partnerName}»` : ''} — план читается по общему пулу партнёра`
    : 'Реестровая площадка офлайн-набора';
}

interface VenueKindHolder {
  kind?: VenueKind;
  partnerName?: string | null;
}

export function VenuesPage() {
  const { params } = useQueryParams();
  const { id, list } = cardParams(params);

  // One route, two screens. The card keeps the roster state in the URL, so
  // closing it (dropping `id`) lands the user exactly where they drilled from.
  return id !== null ? <VenueCard venueId={id} listParams={list} /> : <VenuesRoster params={list} />;
}

// ---------------------------------------------------------------------------
// The roster (#6)
// ---------------------------------------------------------------------------

function VenuesRoster({ params }: { params: VenuesListParams }) {
  const { setParams } = useQueryParams();
  const query = useVenues(toListQuery(params));
  const columns = useVisibleColumns();
  const headerColumns = useMemo(() => rosterHeaderColumns(columns.visible), [columns.visible]);
  // T.R6 — #6 carries the manager as an `AppUser.id`; the #25.5 directory (VIEWER) joins
  // it to a name for the «Менеджер» column. One fetch per session, and a failure degrades
  // to the bare id the column printed before the join existed.
  // ADDITIVE (T.Z42) — the SAME read now carries each manager's `department`, which is
  // what the «Отдел» filter joins against: #25 (the users roster) is ADMIN-only and this
  // filter has to work for a МЕНЕДЖЕР session.
  const directory = useUserDirectory();
  const departmentsByUser = useMemo(
    () => new Map(directory.map((entry) => [entry.id, entry])),
    [directory],
  );

  // The «Площадка» type-ahead reads the venue DIRECTORY (#6, one page of 200 —
  // names and addresses), the same read the quotas and applications filters use.
  // The roster's own page is NOT the directory: it is filtered and paginated, so
  // a venue the manager could reach by paging would be missing from it.
  const venueDirectory = useVenueDirectory();
  const venueOptions = useMemo(() => venueComboboxOptions(venueDirectory.data ?? []), [venueDirectory.data]);

  const rowsOfKind: VenueRow[] = venuesOfKind(query.data?.venues ?? [], params.kind);
  // ADDITIVE (T.Z42) — the «Отдел» filter narrows on top of the kind: the bucket is
  // derived per venue from the managers' departments (client-side join — see the
  // directory note above). «Все» keeps the rows untouched.
  const venues: VenueRow[] = venuesOfDepartment(rowsOfKind, departmentsByUser, params.department);
  const meta = query.data?.meta;
  const pageSize = meta?.pageSize ?? 50;
  // The counts on the options come from the same rows the «Тип» filter works on
  // (the directory read covers the whole roster), so an option never promises a
  // count it cannot keep.

  // `region`/`q` are committed on submit, not on every keystroke: each commit
  // is a server round-trip (#6 takes both), and a pager click must not drag a
  // half-typed needle with it.
  const [regionDraft, setRegionDraft] = useState(params.region ?? '');
  const [qDraft, setQDraft] = useState(params.q ?? '');
  const filtersOn = Boolean(
    params.region ||
      params.q ||
      params.venueId !== undefined ||
      params.kind !== undefined ||
      params.department !== undefined ||
      params.page > 1,
  );

  /**
   * One commit = the whole filter state. `kind` is part of the patch on purpose
   * (A5/T.Z18) — a caller that leaves it out CLEARS the type filter, so the two
   * sites that only move `region`/`q`/`venueId` pass the current `params.kind`
   * through; the reset is the one caller that wants it gone.
   */
  const applyFilters = (next: {
    region?: string;
    q?: string;
    venueId?: number;
    kind?: VenueKind;
    department?: DepartmentBucket;
  }) => {
    setRegionDraft(next.region ?? '');
    setQDraft(next.q ?? '');
    setParams({
      [PARAM_REGION]: next.region,
      [PARAM_Q]: next.q,
      [PARAM_VENUE_ID]: next.venueId === undefined ? undefined : String(next.venueId),
      [PARAM_KIND]: next.kind,
      [PARAM_DEPARTMENT]: next.department,
      // A new filter is a new result set: start from the first page.
      [PARAM_PAGE]: undefined,
    });
  };

  return (
    <>
      <div className="page-header">
        <h1>Площадки</h1>
        <p className="page-header__sub">Реестр площадок: регионы, менеджеры, статус контроля.</p>
      </div>

      {/* T.F5: the «Онлайн-обучение» pivot sits ABOVE the roster — it is the report a curator
          opens before diving into a venue, and it does not take the roster's filters (#6 takes
          region/q, the block aggregates the whole ONLINE slice by design). */}
      <OnlinePivotBlock />

      <div className="card card--flush venue-section">
        <form
          className="toolbar venues-toolbar venues-toolbar--filters"
          data-testid="venues-filter"
          onSubmit={(event) => {
            event.preventDefault();
            applyFilters({
              region: regionDraft.trim(),
              q: qDraft.trim(),
              kind: params.kind,
              department: params.department,
            });
          }}
        >
          {/* Wave 6 (owner) — the venue field leads the bar, on a full-width row of
              its own: a name plus an address is the longest label on the page, and
              the roster's first question is «какая площадка?». Commits straight to
              the URL, so a pager click cannot drop it (the type-ahead is not a
              draft, unlike the two committed-on-submit fields below). */}
          <div className="toolbar__field toolbar__field--venue">
            <label className="toolbar__label" htmlFor="venues-venue">
              Площадка
            </label>
            <Combobox
              id="venues-venue"
              testId="venues-venue"
              value={params.venueId === undefined ? '' : String(params.venueId)}
              options={venueOptions}
              onChange={(next) =>
                applyFilters({
                  region: params.region,
                  q: params.q,
                  venueId: next === '' ? undefined : Number.parseInt(next, 10),
                  kind: params.kind,
                  department: params.department,
                })
              }
              placeholder="Все площадки — найдите по названию или адресу"
              emptyText="Площадка не найдена"
              clearLabel="Показать все площадки"
              resultsLabel={(count) => `Найдено площадок: ${count}`}
            />
          </div>
          {/* T.V4 — one flow, read left to right: the two committed-on-submit
              fields, then «Применить» straight after them (it used to hang on a
              row of its own, pushed to the right edge), then the column picker,
              the counter and the export at the far end of the same line. */}
          <div className="toolbar__row">
            <div className="toolbar__field">
              <label className="toolbar__label" htmlFor="venues-region">
                Регион
              </label>
              <Input
                id="venues-region"
                data-testid="venues-region"
                value={regionDraft}
                placeholder="Москва"
                onChange={(event) => setRegionDraft(event.target.value)}
              />
            </div>
            <div className="toolbar__field">
              <label className="toolbar__label" htmlFor="venues-q">
                Поиск
              </label>
              <Input
                id="venues-q"
                data-testid="venues-q"
                value={qDraft}
                placeholder="название или адрес"
                onChange={(event) => setQDraft(event.target.value)}
              />
            </div>
            {/* A5/T.Z18 — the «Тип» filter: the reporting kind the rows already carry.
                It commits like the «Площадка» type-ahead (straight into the URL), and
                its options print how many venues of each kind the directory (#6) knows.
                #6 itself has no kind parameter, so the narrowing is the browser's —
                `toListQuery` raises the page size to #6's ceiling for the request, and
                `venuesOfKind` drops the rows that do not match. */}
            <KindFilter params={params} directory={venueDirectory.data} onFilter={applyFilters} />
            {/* ADDITIVE (T.Z42) — the «Отдел» filter, the same client-side pattern as
                «Тип» (see toListQuery): it raises the page size to #6's ceiling and
                restarts at page 1, so the narrowing covers the WHOLE roster and the
                counter below is the true count. The buckets are the managers'
                departments; «Смешанный» is a venue with managers of both. */}
            <DepartmentFilter params={params} directory={venueDirectory.data} usersById={departmentsByUser} onFilter={applyFilters} />
            <button type="submit" className="btn btn--primary btn--sm" data-testid="venues-apply">
              Применить
            </button>
            {filtersOn ? (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                data-testid="venues-reset"
                onClick={() => applyFilters({})}
              >
                Сбросить
              </button>
            ) : null}
            <p className="toolbar__spacer" />
            <ColumnPicker visible={columns.visible} onToggle={columns.toggle} onReset={columns.reset} />
            <p className="toolbar__meta" data-testid="venues-total">
              {/* With «Тип» or «Отдел» set the counter is the filtered count: the
                  server's `meta.total` knows neither (see `toListQuery`), and the whole
                  roster is on screen by then, so `venues.length` IS the truth. */}
              {venueCountLabel(
                params.kind !== undefined || params.department !== undefined
                  ? venues.length
                  : (meta?.total ?? venues.length),
              )}
            </p>
            {/* T.E1 — the roster plus its aggregates, scoped by the same two filters.
                The button is a `type="button"` control inside this form on purpose:
                a submit would re-run the search, not export. `kind` does NOT travel
                (A5/T.Z18): the #26 venues file takes `region`/`q` only and 400s an
                unknown key, and a silently swallowed filter would hand the manager a
                file that is not the view they were looking at. */}
            <ExportButton
              resource="venues"
              testId="venues-export"
              params={{ region: params.region, q: params.q, venueId: params.venueId }}
            />
          </div>
        </form>

        {query.isPending ? (
          <div className="venue-section__body">
            <RosterSkeleton />
          </div>
        ) : query.isError ? (
          <div className="venue-section__body">
            <ErrorState
              message={describeFailure(query.error as ApiError | null, 'Не удалось загрузить площадки.')}
              onRetry={() => void query.refetch()}
            />
          </div>
        ) : venues.length === 0 ? (
          <EmptyState
            title="Площадок не найдено"
            hint={
              filtersOn
                ? 'Фильтр ничего не отобрал — ослабьте условие.'
                : 'Импортируйте квоты, чтобы завести реестр.'
            }
            action={
              filtersOn ? (
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => applyFilters({})}>
                  Сбросить фильтр
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
            <TableWrap>
              <table className="data" data-testid="venues-table">
                <thead>
                  <tr>
                    <th>Площадка</th>
                    {rosterColumnLabels(headerColumns).map((label, index) => (
                      <th key={headerColumns[index]!}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {venues.map((venue) => (
                    <tr key={venue.id} data-testid="venue-row" data-venue-id={venue.id} data-kind={venue.kind ?? 'REGISTRY'}>
                      <td className="cell-primary">
                        <Link to={venueCardPath(venue.id, params)} className="link-btn" data-testid="venue-link">
                          {venue.name}
                        </Link>
                        {/* Wave 9 (T.Y9) — the reporting kind rides the name: a partner
                            filial reads nothing like a registry venue in every other
                            column, and the badge is what stops a manager reading its
                            «план» as the venue's own. */}
                        <span className="venue-kind" data-testid={`venue-kind-${venue.id}`}>
                          <Badge tone={isPartnerVenue(venue) ? 'warn' : 'neutral'} title={venueKindTitle(venue)}>
                            {venueKindLabel(venue.kind)}
                          </Badge>
                        </span>
                      </td>
                      {headerColumns.map((column) => (
                        <VenueCell key={column} venue={venue} column={column} directory={directory} />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
            <Pagination
              meta={{
                page: meta?.page ?? params.page,
                totalPages: totalPages(meta?.total ?? 0, pageSize),
                total: meta?.total ?? 0,
                limit: pageSize,
              }}
              onPage={(page) => setParams({ [PARAM_PAGE]: page > 1 ? page : undefined })}
            />
          </>
        )}
      </div>
      <p className="venue-footnote">Нажмите на площадку, чтобы открыть её карточку.</p>
    </>
  );
}

/**
 * A5/T.Z18 — the roster's «Тип» filter: «Все / Офлайн / Онлайн-филиалы», i.e. the
 * venue's reporting `kind` (`REGISTRY` / `PARTNER_ONLINE`) that every row and every
 * directory entry already carries. The counts on the options come from the directory
 * read the page makes anyway; when that read failed or came back empty the options
 * keep their bare labels instead of a fabricated «(0)».
 */
function KindFilter({
  params,
  directory,
  onFilter,
}: {
  params: VenuesListParams;
  directory: ReadonlyArray<{ kind?: VenueKind }> | undefined;
  onFilter: (next: { region?: string; q?: string; venueId?: number; kind?: VenueKind }) => void;
}) {
  const options = venueKindFilterOptions(venueKindCounts(directory));
  const value = params.kind ?? '';
  return (
    <div className="toolbar__field">
      <label className="toolbar__label" htmlFor="venues-kind">
        Тип
      </label>
      <Select
        id="venues-kind"
        data-testid="venues-kind"
        value={value}
        onChange={(event) =>
          onFilter({
            region: params.region,
            q: params.q,
            venueId: params.venueId,
            kind: parseKindParam(event.target.value),
          })
        }
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} title={option.title}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

/**
 * ADDITIVE (T.Z42, v1.8.7) — the roster's «Отдел» filter: «Все» / «Отдел привлечения» /
 * «Отдел сопровождения» / «Без отдела» / «Смешанный». The full names are the owner's
 * wording; the ОП/ОС codes are storage-only. The counts come from the #6 directory rows
 * joined against the #25.5 directory's `department` (the map the page builds once), so
 * an option shows how many venues it selects; a failed or absent read keeps bare labels
 * instead of a fabricated «(0)».
 */
function DepartmentFilter({
  params,
  directory,
  usersById,
  onFilter,
}: {
  params: VenuesListParams;
  directory: ReadonlyArray<VenueManagersLike> | undefined;
  usersById: ReadonlyMap<number, DepartmentSource>;
  onFilter: (next: { department?: DepartmentBucket } & Record<string, unknown>) => void;
}) {
  const counts = directory === undefined || usersById.size === 0 ? null : countVenueDepartments(directory, usersById);
  const options = departmentFilterOptions(counts);
  return (
    <div className="toolbar__field">
      <label className="toolbar__label" htmlFor="venues-department">
        Отдел
      </label>
      <Select
        id="venues-department"
        data-testid="venues-department"
        value={params.department ?? ''}
        onChange={(event) =>
          onFilter({
            region: params.region,
            q: params.q,
            venueId: params.venueId,
            kind: params.kind,
            department: parseDepartmentBucket(event.target.value),
          })
        }
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} title={option.title}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** Same shape the table stands in for, so nothing jumps when the data lands. */
function RosterSkeleton() {
  return (
    <div data-testid="venues-skeleton" className="venue-skeleton">
      {Array.from({ length: 6 }, (_, index) => (
        <span className="venue-skeleton__line" key={index} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The column picker (T.R9)
// ---------------------------------------------------------------------------

/**
 * The roster's visible columns, persisted per browser. The stored string is the
 * only state: read once on mount, written on every toggle. A second tab is
 * allowed to disagree until it reloads — the picker is the only writer.
 *
 * Storage itself is defensive: a `localStorage` that throws (private mode, a
 * denied permission) degrades to an in-memory selection for this page view, so
 * the table keeps working and only the persistence is lost.
 */
function useVisibleColumns(): {
  visible: ReadonlyArray<RosterColumnId>;
  toggle: (id: RosterColumnId) => void;
  reset: () => void;
} {
  const read = useCallback((): string | null => {
    try {
      return window.localStorage.getItem(ROSTER_COLUMNS_STORAGE_KEY);
    } catch {
      return null;
    }
  }, []);

  const [visible, setVisible] = useState<ReadonlyArray<RosterColumnId>>(() => parseVisibleColumns(read()));

  const persist = useCallback(
    (columns: ReadonlyArray<RosterColumnId>): void => {
      try {
        window.localStorage.setItem(ROSTER_COLUMNS_STORAGE_KEY, serializeVisibleColumns(columns));
      } catch {
        // Persistence is best-effort: the on-screen table already has the value.
      }
    },
    [],
  );

  const toggle = useCallback(
    (id: RosterColumnId) => {
      setVisible((current) => {
        const next = current.includes(id) ? current.filter((column) => column !== id) : [...current, id];
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const reset = useCallback(() => {
    setVisible(DEFAULT_ROSTER_COLUMNS);
    persist(DEFAULT_ROSTER_COLUMNS);
  }, [persist]);

  return { visible, toggle, reset };
}

/** The «Колонки» dropdown: one checkbox per column, the choice remembered. */
function ColumnPicker({
  visible,
  onToggle,
  onReset,
}: {
  visible: ReadonlyArray<RosterColumnId>;
  onToggle: (id: RosterColumnId) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const customised = visible.length !== DEFAULT_ROSTER_COLUMNS.length || DEFAULT_ROSTER_COLUMNS.some((id) => !visible.includes(id));

  return (
    <div className="column-picker" ref={rootRef}>
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        data-testid="venue-columns-toggle"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        Колонки
      </button>
      {open ? (
        <div className="column-picker__menu" data-testid="venue-columns-menu">
          {ROSTER_COLUMNS.map((column) => (
            <label key={column.id} className="column-picker__item">
              <input
                type="checkbox"
                data-testid={`venue-columns-${column.id}`}
                checked={visible.includes(column.id)}
                onChange={() => onToggle(column.id)}
              />
              <span className="column-picker__label">{column.label}</span>
              {column.hint ? <span className="column-picker__hint">{column.hint}</span> : null}
            </label>
          ))}
          <div className="column-picker__foot">
            {customised ? (
              <button type="button" className="btn btn--ghost btn--sm" data-testid="venue-columns-reset" onClick={onReset}>
                Сбросить
              </button>
            ) : null}
            <span className="column-picker__note">Запоминается в браузере</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** One body cell of the roster, keyed by the column id the picker manages. */
function VenueCell({
  venue,
  column,
  directory,
}: {
  venue: VenueRow;
  column: RosterColumnId;
  directory: ReturnType<typeof useUserDirectory>;
}) {
  switch (column) {
    case 'address':
      return (
        <td className={venue.normalizedAddress ? undefined : 'cell-muted'} data-testid="row-address">
          {venue.normalizedAddress ?? '—'}
        </td>
      );
    case 'region':
      return (
        <td className={venue.region ? undefined : 'cell-muted'} data-testid="row-region">
          {venue.region ?? '—'}
        </td>
      );
    case 'manager': {
      // T.R6 — the joined name; the id survives as the tooltip (and as the fallback).
      const manager = managerCell(venue.managerUserId, directory);
      return (
        <td
          className={venue.managerUserId === null ? 'cell-muted' : undefined}
          data-testid="row-manager"
          title={manager.title}
        >
          {manager.text}
        </td>
      );
    }
    case 'curator':
      return (
        <td className={venue.curatorName ? undefined : 'cell-muted'} data-testid="row-curator">
          {curatorNameLabel(venue)}
        </td>
      );
    case 'programsCount':
      return (
        <td className="num" data-testid="row-programs">
          {venue.programsCount ?? 0}
        </td>
      );
    case 'control':
      return (
        <td className={venue.controlStatus ? undefined : 'cell-muted'} data-testid="row-control">
          {controlStatusLabel(venue.controlStatus)}
        </td>
      );
    case 'applications':
      return (
        <td data-testid="row-statuses">
          <VenueStatusBadges counts={rowStatusCounts(venue)} testIdPrefix={`row-${venue.id}`} />
        </td>
      );
    case 'case':
      return (
        <td className="cell-wrap" data-testid="row-case">
          {sheetCellLabel(venue.caseInfo)}
        </td>
      );
    case 'freed':
      return (
        <td className="cell-wrap" data-testid="row-freed">
          {sheetCellLabel(venue.freedQuotaNote)}
        </td>
      );
    case 'approval':
      return (
        <td className="cell-wrap" data-testid="row-approval">
          {sheetCellLabel(venue.extraApproval)}
        </td>
      );
    case 'comment':
      return (
        <td className="cell-wrap" data-testid="row-comment">
          {sheetCellLabel(venue.commentImported)}
        </td>
      );
    case 'programsSelection':
      return (
        <td className="cell-wrap" data-testid="row-programs-selection">
          {sheetCellLabel(venue.programsSelection)}
        </td>
      );
    case 'enrolled':
      return (
        <td className="num" data-testid="row-enrolled">
          {sheetCellLabel(venue.enrolledCount)}
        </td>
      );
    case 'reportUrl':
      return (
        <td className="cell-wrap" data-testid="row-report-url">
          {venue.curatorReportUrl ? (
            <a href={venue.curatorReportUrl} target="_blank" rel="noreferrer">
              Отчёт
            </a>
          ) : (
            '—'
          )}
        </td>
      );
  }
}
